import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PopularTours = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const navigate = useNavigate();

  const categories = [
    { id: 'all', label: 'All Tours', icon: 'Globe' },
    { id: 'adventure', label: 'Adventure', icon: 'Mountain' },
    { id: 'cultural', label: 'Cultural', icon: 'Building' },
    { id: 'nature', label: 'Nature', icon: 'Trees' },
    { id: 'food', label: 'Food & Drink', icon: 'UtensilsCrossed' },
    { id: 'beach', label: 'Beach', icon: 'Waves' }
  ];

  const popularTours = [
    {
      id: 1,
      title: "Bali Temple Hopping & Rice Terraces",
      location: "Bali, Indonesia",
      image: "https://images.unsplash.com/photo-1555400082-6c3b6d6d5b6b?w=800&h=600&fit=crop",
      duration: "8 hours",
      groupSize: "12 people max",
      price: 89,
      originalPrice: 120,
      rating: 4.9,
      reviews: 234,
      category: 'cultural',
      highlights: ["Tanah Lot Temple", "Jatiluwih Rice Terraces", "Traditional Lunch"],
      badge: "Best Seller",
      badgeColor: "bg-accent",
      discount: 26
    },
    {
      id: 2,
      title: "Santorini Sunset Sailing Experience",
      location: "Santorini, Greece",
      image: "https://images.pexels.com/photos/2901209/pexels-photo-2901209.jpeg?w=800&h=600&fit=crop",
      duration: "5 hours",
      groupSize: "8 people max",
      price: 145,
      originalPrice: 180,
      rating: 4.8,
      reviews: 189,
      category: 'adventure',
      highlights: ["Luxury Catamaran", "Sunset Views", "Greek Dinner"],
      badge: "Premium",
      badgeColor: "bg-primary",
      discount: 19
    },
    {
      id: 3,
      title: "Tokyo Street Food & Night Markets",
      location: "Tokyo, Japan",
      image: "https://images.pixabay.com/photo/2020/05/18/16/17/cherry-blossoms-5187041_1280.jpg?w=800&h=600&fit=crop",
      duration: "4 hours",
      groupSize: "10 people max",
      price: 75,
      originalPrice: 95,
      rating: 4.7,
      reviews: 156,
      category: 'food',
      highlights: ["Shibuya Crossing", "Local Street Food", "Night Markets"],
      badge: "Local Favorite",
      badgeColor: "bg-secondary",
      discount: 21
    },
    {
      id: 4,
      title: "Amazon Rainforest Wildlife Safari",
      location: "Manaus, Brazil",
      image: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=800&h=600&fit=crop",
      duration: "3 days",
      groupSize: "6 people max",
      price: 420,
      originalPrice: 520,
      rating: 4.9,
      reviews: 98,
      category: 'nature',
      highlights: ["Wildlife Spotting", "River Cruise", "Indigenous Village"],
      badge: "Eco-Friendly",
      badgeColor: "bg-success",
      discount: 19
    },
    {
      id: 5,
      title: "Maldives Island Hopping Adventure",
      location: "Maldives",
      image: "https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?w=800&h=600&fit=crop",
      duration: "Full day",
      groupSize: "15 people max",
      price: 199,
      originalPrice: 250,
      rating: 4.8,
      reviews: 267,
      category: 'beach',
      highlights: ["Snorkeling", "Dolphin Watching", "Beach BBQ"],
      badge: "Trending",
      badgeColor: "bg-warning",
      discount: 20
    },
    {
      id: 6,
      title: "Himalayan Base Camp Trek",
      location: "Nepal",
      image: "https://images.pixabay.com/photo/2018/01/15/08/25/everest-3084966_1280.jpg?w=800&h=600&fit=crop",
      duration: "14 days",
      groupSize: "8 people max",
      price: 1299,
      originalPrice: 1599,
      rating: 4.9,
      reviews: 145,
      category: 'adventure',
      highlights: ["Everest Base Camp", "Sherpa Culture", "Mountain Views"],
      badge: "Epic Adventure",
      badgeColor: "bg-accent",
      discount: 19
    }
  ];

  const filteredTours = activeCategory === 'all' 
    ? popularTours 
    : popularTours?.filter(tour => tour?.category === activeCategory);

  const handleTourClick = (tour) => {
    navigate(`/tour-detail?id=${tour?.id}`);
  };

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
  };

  const handleViewAllClick = () => {
    navigate('/search-results');
  };

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Popular Tours & Experiences
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Join thousands of travelers who have already booked these amazing experiences
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories?.map((category) => (
            <button
              key={category?.id}
              onClick={() => handleCategoryClick(category?.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                activeCategory === category?.id
                  ? 'bg-primary text-white shadow-lg'
                  : 'bg-white text-muted-foreground hover:bg-primary/10 hover:text-primary border border-border'
              }`}
            >
              <Icon name={category?.icon} size={16} />
              <span>{category?.label}</span>
            </button>
          ))}
        </div>

        {/* Tours Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {filteredTours?.map((tour, index) => (
            <div
              key={tour?.id}
              className="group bg-card rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer hover-lift animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => handleTourClick(tour)}
            >
              {/* Image Container */}
              <div className="relative h-56 overflow-hidden">
                <Image
                  src={tour?.image}
                  alt={tour?.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                
                {/* Badge */}
                <div className={`absolute top-4 left-4 ${tour?.badgeColor} text-white px-3 py-1 rounded-full text-sm font-medium`}>
                  {tour?.badge}
                </div>

                {/* Discount */}
                {tour?.discount && (
                  <div className="absolute top-4 right-4 bg-accent text-white px-2 py-1 rounded-md text-sm font-bold">
                    -{tour?.discount}%
                  </div>
                )}

                {/* Wishlist Button */}
                <button className="absolute bottom-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white">
                  <Icon name="Heart" size={18} className="text-muted-foreground hover:text-accent" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-1">
                      {tour?.title}
                    </h3>
                    <div className="flex items-center text-muted-foreground text-sm">
                      <Icon name="MapPin" size={14} className="mr-1" />
                      {tour?.location}
                    </div>
                  </div>
                </div>

                {/* Tour Details */}
                <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={14} />
                    <span>{tour?.duration}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Users" size={14} />
                    <span>{tour?.groupSize}</span>
                  </div>
                </div>

                {/* Highlights */}
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1">
                    {tour?.highlights?.slice(0, 2)?.map((highlight, idx) => (
                      <span key={idx} className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                        {highlight}
                      </span>
                    ))}
                    {tour?.highlights?.length > 2 && (
                      <span className="text-xs text-muted-foreground px-2 py-1">
                        +{tour?.highlights?.length - 2} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Rating and Price */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center space-x-1">
                      <Icon name="Star" size={14} className="text-warning fill-current" />
                      <span className="text-sm font-medium text-foreground">{tour?.rating}</span>
                      <span className="text-sm text-muted-foreground">({tour?.reviews})</span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    {tour?.originalPrice && (
                      <div className="text-sm text-muted-foreground line-through">${tour?.originalPrice}</div>
                    )}
                    <div className="text-xl font-bold text-primary">${tour?.price}</div>
                  </div>
                </div>

                {/* Book Now Button */}
                <div className="mt-4 pt-4 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Calendar"
                    iconPosition="left"
                    className="w-full group-hover:bg-primary group-hover:text-white transition-colors"
                  >
                    Book Now
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Button
            variant="outline"
            size="lg"
            onClick={handleViewAllClick}
            iconName="ArrowRight"
            iconPosition="right"
          >
            View All Tours
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PopularTours;